import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, LogOut, Timer, CalendarCheck, CalendarClock, FileClock, ChevronDown, ChevronUp } from "lucide-react";

import { DropdownSelect } from "../components/DropdownButton.js";
import { PipelineCell, ColHeader } from "../components/PipelineCell.js";
import { Skeleton } from "../components/Skeleton.js";
import { Input } from "../components/ui/input.js";
import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { fetchCached, getCached, invalidateCached } from "../lib/requestCache.js";
import { cn } from "../lib/utils.js";
import { useToast } from "../context/ToastContext.js";

function getUserId(): string {
  try {
    const token = getAccessToken() ?? "";
    return String((JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>)["sub"] ?? "");
  } catch { return ""; }
}

const IST = "Asia/Kolkata";

/** "24 Oct" -- short enough for a caption, unambiguous about which day. */
function shortDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "UTC" });
}
// Gate Register column widths -- the same pipeline-row layout My History and
// Weekly Hours use, so the three attendance lists read as one component.
const GATE_COLS = { employee: 190, department: 150, office: 130, checkIn: 115, checkOut: 115, duration: 130 };

export const DEFAULT_SHIFT_HOURS = 9; // mirrors attendance.service.ts's fallback when no shift is assigned
import { useMe, useMeLoading } from "../context/MeContext.js";
import { usePageBack } from "../context/PageBackContext.js";
import { ManagedInADP } from "../components/ManagedInADP.js";
import { HR_ROLES, MGR_ROLES } from "@hrms/types";
import { SegmentedTabs } from "../components/SegmentedTabs.js";
import { TeamMemberDetailPanel } from "../components/TeamMemberDetailPanel.js";
import { MonthlyAttendanceGrid } from "../components/MonthlyAttendanceGrid.js";
import { TeamMemberCard, TeamMemberCardSkeleton } from "../components/TeamMemberCard.js";
import { AttendanceHistoryTimeline } from "../components/AttendanceHistoryTimeline.js";
import { WeeklyBiometricAttendance } from "../components/WeeklyBiometricAttendance.js";
import MyShiftRequestsCard from "../components/MyShiftRequestsCard.js";
import { GlowCard } from "../components/ui/glow-card.js";
import { Badge } from "../components/ui/badge.js";
import { WeeklyHoursTimeline } from "../components/WeeklyHoursTimeline.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { isoWeekLabel, lastCompletedMonday, shiftWeek, monthOfWeek, firstWeekOfMonth } from "../lib/isoWeek.js";
import { type HistoryPeriod, currentAnchor, shiftAnchor, rangeFor } from "../lib/historyRange.js";
import type { ShiftRecord } from "../components/employee-form/sections/WorkScheduleSection.js";
import { SpecularButton } from "../components/ui/specular-button.js";
import { notifyAttendanceChanged, attendanceActions, useTodayAttendance } from "../hooks/useTodayAttendance.js";
import { useTeamMemberDetail } from "../hooks/useTeamMemberDetail.js";
import { type FloodCardTone } from "../components/ui/flood-card.js";
import { SummaryStatCard, SummaryStatSuffix } from "../components/SummaryStatCard.js";

export interface AttendanceRecord {
  id: string; date: string; checkIn: string | null; checkOut: string | null;
  source: string; isLate: boolean; notes: string | null; location: string | null;
  shiftHours: number | null;
  isToday: boolean;
  isYesterday?: boolean;
  /** Closed as a missed check-out: no check-out time until a correction supplies one. */
  autoClosedAt?: string | null;
  isLeave?: boolean;
  isAbsent?: boolean;
  /** For a weekend, holiday or leave day that was worked: whether it is still awaiting approval. */
  exceptionState?: "not_required" | "pending" | "approved" | "declined";
}
export interface MyCorrection { id: string; date: string; status: string; requestedCheckIn: string | null; requestedCheckOut: string | null; reason: string; approverRole: string | null; approverRemark: string | null; }
interface DayBreakdown { date: string; actualHours: number; expectedHours: number; onLeave: boolean; isHoliday: boolean; }
interface WeeklyHoursSelf {
  weekStart: string; weekEnd: string;
  totalHours: number; expectedHours: number;
  status: string | null; approverRemark: string | null;
  days: DayBreakdown[];
}
export interface WeeklyHistoryDay {
  date: string; actualHours: number; expectedHours: number;
  onLeave: boolean; isHoliday: boolean; open: boolean;
}
export interface WeeklyHoursHistoryRow {
  weekStart: string; weekEnd: string;
  totalHours: number; expectedHours: number;
  days: WeeklyHistoryDay[];
  status: string | null; approverRole: string | null; approverRemark: string | null;
  /** Short of the expected hours after the rule's grace: only these weeks take a correction. */
  short: boolean;
  correctionStatus: string | null;
  correctionId: string | null; requestedTotalHours: number | null; correctionReason: string | null;
}
export interface MemberHistoryItem {
  key: string;
  label: string;
  status: string;
  approverRole: string | null;
  remark: string | null;
  sortDate: string;
}
interface GateEntry {
  id: string; date: string; checkIn: string | null; checkOut: string | null; isLate: boolean;
  shiftHours: number | null;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string; department: { name: string } | null; office: { id: string; name: string } | null };
}
interface OfficeOption { id: string; name: string; }
interface AttendanceSlot { checkIn: string | null; checkOut: string | null; }
interface WeekSummary { totalHours: number; expectedHours: number; status: string | null; }
export interface TeamOverviewRow {
  id: string; firstName: string; lastName: string; employeeCode: string; designation: string;
  /**
   * The person's own office. Only the department endpoints send it — there, a
   * department's people are routinely spread across branches, so the card has to say
   * which one each of them is in.
   */
  office?: { id: string; name: string } | null;
  /**
   * Who this person reports to. Sent only for the roster's "Other Members" group, whose
   * members sit outside every manager in the department but usually do have a manager —
   * one elsewhere, or one whose role carries no approval authority. `null` is the real
   * "nobody assigned" case; `undefined` means this caller doesn't ask for it.
   */
  reportsTo?: { name: string; departmentName: string | null; kind: "reporting" | "hr" } | null;
  today: AttendanceSlot | null;
  thisWeek: WeekSummary;
  directReportCount: number;
  teamHours: { totalHours: number; expectedHours: number };
  pendingCorrections: number; pendingWeeklyHours: number;
  /** Only the Team tab sends it; the department roster leaves it out. */
  pendingShiftRequests?: number;
  /** Only the Team tab sends it; the department roster leaves it out. */
  pendingApprovals?: number;
}
/** Everything waiting on the viewer for this person: the Team tab's list and the card count both use it. */
export function pendingRequestTotal(row: TeamOverviewRow): number {
  return row.pendingCorrections + row.pendingWeeklyHours + (row.pendingShiftRequests ?? 0) + (row.pendingApprovals ?? 0);
}
/** Whether this person has working hours at all, and whether they have had to ask. */
export interface MemberShiftState {
  hasShift: boolean;
  shiftStartHHMM: string | null;
  shiftEndHHMM: string | null;
  nudgeSentAt: string | null;
}

export interface TeamMemberDetail {
  member: TeamOverviewRow & { managerName: string | null; shift?: MemberShiftState };
  directReports: Array<Omit<TeamOverviewRow, "directReportCount" | "teamHours" | "pendingCorrections" | "pendingWeeklyHours">>;
}

export function fmt(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: IST });
}
/**
 * The check-out to show for one day.
 *
 * Someone who checked in and never checked out on a past day forgot to punch out --
 * their day is shown as check-in plus their shift length (9h when no shift is
 * assigned), flagged `assumed` so it is never read as a real punch. Today's or
 * yesterday's (night shift) open record is left alone while the missed check-out job
 * has not closed it: that person is still at work, not forgetful.
 */
export function effectiveCheckOut(
  r: { checkIn: string | null; checkOut: string | null; shiftHours: number | null; isToday?: boolean; isYesterday?: boolean; autoClosedAt?: string | null },
): { iso: string | null; assumed: boolean; shiftHours: number } {
  const shiftHours = r.shiftHours ?? DEFAULT_SHIFT_HOURS;
  if (r.checkOut) return { iso: r.checkOut, assumed: false, shiftHours };
  if (!r.checkIn || ((r.isToday || r.isYesterday) && !r.autoClosedAt)) return { iso: null, assumed: false, shiftHours };
  return { iso: new Date(new Date(r.checkIn).getTime() + shiftHours * 3_600_000).toISOString(), assumed: true, shiftHours };
}
export function duration(checkIn: string | null, checkOut: string | null) {
  if (!checkIn || !checkOut) return "—";
  const mins = Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 60000);
  if (mins < 0) return "—";
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}
export function shiftDelta(checkIn: string | null, checkOut: string | null, shiftHours: number | null) {
  if (!checkIn || !checkOut || shiftHours == null) return null;
  const mins = Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 60000);
  const diffMins = mins - Math.round(shiftHours * 60);
  if (Math.abs(diffMins) < 5) return null;
  const sign = diffMins > 0 ? "+" : "−";
  const abs = Math.abs(diffMins);
  return `${sign}${Math.floor(abs / 60)}h${String(abs % 60).padStart(2, "0")}m vs ${shiftHours}h shift`;
}
// tone / textClass: the same status as a FloodCard tone and a Tailwind text color.
/** `short` is what the dense timelines print; `label` is the full text, shown on hover. */
export interface WeeklyStatusDisplay { label: string; short: string; fg: string; bg: string; barColor: string; tone: FloodCardTone; textClass: string; }
export function weeklyStatusDisplay(status: string | null, totalHours: number | null, expectedHours: number | null): WeeklyStatusDisplay {
  if (status === "APPROVED") return { label: "Approved", short: "Approved", fg: "var(--color-success-fg)", bg: "var(--color-success-bg)", barColor: "var(--color-success)", tone: "success", textClass: "text-[var(--color-success-fg)]" };
  if (status === "REJECTED") return { label: "Declined", short: "Declined", fg: "var(--color-error-fg)", bg: "var(--color-error-bg)", barColor: "var(--color-error)", tone: "danger", textClass: "text-[var(--color-error-fg)]" };
  if (status === "PENDING") return { label: "Pending Review", short: "Pending", fg: "var(--color-warning-fg)", bg: "var(--color-warning-bg)", barColor: "var(--color-warning)", tone: "warning", textClass: "text-[var(--color-warning-fg)]" };
  if (totalHours != null && expectedHours != null && totalHours >= expectedHours) {
    return { label: "Hours Met", short: "Met", fg: "var(--color-info-fg)", bg: "var(--color-info-bg)", barColor: "var(--color-info)", tone: "info", textClass: "text-[var(--color-info-fg)]" };
  }
  return { label: "Not Submitted", short: "Not Sent", fg: "var(--color-surface-700)", bg: "var(--color-surface-100)", barColor: "var(--color-surface-300)", tone: "brand", textClass: "text-surface-700" };
}
const APPROVER_ROLE_LABELS: Record<string, string> = {
  TEAM_LEAD: "Team Lead",
  MANAGER: "Manager",
  SR_MANAGER: "Senior Manager",
  SUPER_HR_ADMIN: "HR Admin",
  CORPORATE_HR: "HR Admin",
  REGIONAL_HR: "HR Admin",
};
export function approverRoleLabel(role: string | null): string | null {
  if (!role) return null;
  return APPROVER_ROLE_LABELS[role] ?? role;
}

/**
 * Every role named exactly, for places that must say which tier acted.
 *
 * Separate from approverRoleLabel on purpose: that one collapses the three HR tiers
 * into "HR Admin", which is right for an approval (the reader does not care which HR
 * desk signed off) but wrong for "who changed my shift" -- there it matters whether
 * Corporate HR or your own manager did it.
 */
const EXACT_ROLE_LABELS: Record<string, string> = {
  TEAM_LEAD: "Team Lead",
  MANAGER: "Manager",
  SR_MANAGER: "Senior Manager",
  SUPER_HR_ADMIN: "Super HR Admin",
  CORPORATE_HR: "Corporate HR",
  REGIONAL_HR: "Regional HR",
  REGIONAL_ADMIN: "Regional Admin",
  PAYROLL_ADMIN: "Payroll Admin",
};

/** Falls back to title-casing the raw role, keeping short words like IT and HR
 *  capitalised, so an unmapped role still reads as a name rather than CORPORATE_IT. */
export function exactRoleLabel(role: string | null): string | null {
  if (!role) return null;
  const known = EXACT_ROLE_LABELS[role];
  if (known) return known;
  return role
    .split("_")
    .map((w) => (w.length <= 2 ? w : w.charAt(0) + w.slice(1).toLowerCase()))
    .join(" ");
}

// One palette, two forms: the hex for inline-style call sites, the matching
// Tailwind class for class-based ones (Tailwind needs the literal class name).
const AVATAR_PALETTE = [
  { hex: "#2563eb", bg: "bg-[#2563eb]" },
  { hex: "#059669", bg: "bg-[#059669]" },
  { hex: "#d97706", bg: "bg-[#d97706]" },
  { hex: "#dc2626", bg: "bg-[#dc2626]" },
  { hex: "#7c3aed", bg: "bg-[#7c3aed]" },
  { hex: "#0891b2", bg: "bg-[#0891b2]" },
] as const;
function avatarSwatch(id: string) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]!;
}
export function avatarColor(id: string): string {
  return avatarSwatch(id).hex;
}
export function avatarBgClass(id: string): string {
  return avatarSwatch(id).bg;
}

export interface ListSection { key: string; title: string; count?: number; content: React.ReactNode; }

/** Each section is its own card with a collapsible header, so the queues can be
 * opened and closed independently instead of sharing one scroll area. */
function CollapsibleListSection({ section }: { section: ListSection }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-surface-200)] bg-[var(--color-surface-0)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 border-none bg-transparent px-4 py-3 text-left"
      >
        <ChevronDown
          size={14}
          className={cn("shrink-0 text-[var(--color-surface-500)] transition-transform duration-200", !open && "-rotate-90")}
        />
        <span className="text-[13px] font-bold text-[var(--color-surface-700)]">{section.title}</span>
        {section.count != null && section.count > 0 && (
          <span className="rounded-[var(--radius-full)] bg-[var(--color-surface-100)] px-2 py-0.5 text-[11px] font-semibold text-[var(--color-surface-700)]">
            {section.count}
          </span>
        )}
      </button>
      {/* A floor as well as a ceiling: short queues still read as a proper list
          area instead of a one-row sliver, long ones scroll inside the card. */}
      {open && <div className="min-h-[180px] max-h-[480px] overflow-y-auto px-4 pb-4">{section.content}</div>}
    </div>
  );
}

export function MultiListCard({ sections }: { sections: ListSection[] }) {
  if (sections.length === 0) return null;
  return (
    // Full width, like every other section of the member panel, so they share one
    // left and right edge and the panel adapts to the screen it is opened on.
    <div className="flex w-full flex-col gap-[var(--space-3)]">
      {sections.map((s) => <CollapsibleListSection key={s.key} section={s} />)}
    </div>
  );
}

export default function AttendancePage() {
  const { isUS, role, employeeId, attendanceApprovalsEnabled } = useMe();
  const meLoading = useMeLoading();
  const canApprove = HR_ROLES.has(role) || (MGR_ROLES.has(role) && role !== "TEAM_LEAD");
  // Regional HR sees the monthly grid too, scoped server-side to their own office.
  const canViewMonthly = HR_ROLES.has(role);
  // The gate register lists everyone who checked in today across offices, so HR only.
  const canViewGate = HR_ROLES.has(role);
  usePageBack("/dashboard");
  const navigate = useNavigate();
  const toast = useToast();
  const PREFS_KEY = `hrms_prefs_attendance_${getUserId()}`;
  const [tab, setTab] = useState<"my" | "biometric" | "gate" | "weekly" | "shift" | "team" | "monthly">(() => {
    // HR opens on All Team; everyone else on their own history. A saved tab still wins.
    const fallback = HR_ROLES.has(role) ? "monthly" as const : "my" as const;
    try {
      const d = (JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).tab;
      // The restored tab is reconciled against the role, not just the literal --
      // otherwise someone who has lost the role (or edited the pref) lands on a
      // tab that 403s on mount. "departments" is a retired tab and falls through.
      if (d === "monthly") return HR_ROLES.has(role) ? "monthly" : "my";
      if (d === "gate") return HR_ROLES.has(role) ? "gate" : "my";
      return d === "my" || d === "biometric" || d === "weekly" || d === "shift" || d === "team" ? d : fallback;
    } catch { return fallback; }
  });
  // The approval switch arrives with /me/module-access, after the first render, so
  // a restored "weekly" tab cannot be reconciled in the initialiser above the way the
  // role-based ones are. Move off it when the flag lands, mirroring that fallback.
  useEffect(() => {
    if (!attendanceApprovalsEnabled && tab === "weekly") setTab("my");
  }, [attendanceApprovalsEnabled, tab]);

  // Collapsed on every visit, deliberately not remembered: the summary is a glance,
  // the week list under it is the work. Opening it lasts for this visit only.
  const [statsCollapsed, setStatsCollapsed] = useState(true);
  const [history, setHistory] = useState<AttendanceRecord[]>([]);
  const [hasAnyRecords, setHasAnyRecords] = useState(true);
  const [gate, setGate] = useState<GateEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [todayRecord, setTodayRecord] = useState<AttendanceRecord | null>(null);
  const [currentShift, setCurrentShift] = useState<ShiftRecord | null>(null);
  const [gateSearch, setGateSearch] = useState("");
  const [gateOfficeId, setGateOfficeId] = useState("");
  const [historyView, setHistoryView] = useState<"list" | "register">("list");
  // One "which dropdown is open" state for the toolbar: opening one closes any other.
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [myCorrections, setMyCorrections] = useState<MyCorrection[]>([]);
  const [correctionRequestFor, setCorrectionRequestFor] = useState<string | null>(null);
  const [correctionCheckIn, setCorrectionCheckIn] = useState("");
  const [correctionCheckOut, setCorrectionCheckOut] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionBusy, setCorrectionBusy] = useState(false);
  const [correctionErr, setCorrectionErr] = useState("");
  const [historyPeriod, setHistoryPeriod] = useState<HistoryPeriod>("week");
  const [historyAnchor, setHistoryAnchor] = useState(() => currentAnchor("week"));
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [weekStart, setWeekStart] = useState(() => lastCompletedMonday());
  const [myWeekly, setMyWeekly] = useState<WeeklyHoursSelf | null>(null);
  // The month the list shows. Kept in step with the selected week by moveWeek and moveMonth.
  const [weeklyHistoryAnchor, setWeeklyHistoryAnchor] = useState(() => monthOfWeek(weekStart));
  const [weeklyHistory, setWeeklyHistory] = useState<WeeklyHoursHistoryRow[]>([]);
  const [weeklyHasAnyRecords, setWeeklyHasAnyRecords] = useState(true);
  const [weeklyHistoryLoading, setWeeklyHistoryLoading] = useState(false);
  const [weeklyCorrectionRequestFor, setWeeklyCorrectionRequestFor] = useState<string | null>(null);
  const [weeklyCorrectionHours, setWeeklyCorrectionHours] = useState("");
  const [weeklyCorrectionReason, setWeeklyCorrectionReason] = useState("");
  const [weeklyCorrectionBusy, setWeeklyCorrectionBusy] = useState(false);
  const [weeklyCorrectionErr, setWeeklyCorrectionErr] = useState("");
  const [teamOverview, setTeamOverview] = useState<TeamOverviewRow[]>([]);
  const [teamOverviewLoading, setTeamOverviewLoading] = useState(false);
  const pendingTeamMembers = teamOverview.filter((m) => pendingRequestTotal(m) > 0);
  // Until /me/profile resolves the overview request has not been made yet, so show skeletons rather than the empty state.
  const teamBusy = teamOverviewLoading || meLoading;
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const { memberDetail, memberWeeklyCorrections, memberCorrections, memberOvertime, memberShiftDayRequests, memberHistory, reload: reloadMember } = useTeamMemberDetail(selectedMemberId, weekStart);

  // Whether check-in is possible at all: a shift must be assigned in the employee module.
  const { hasShift } = useTodayAttendance();

  // "Fix it" on a missed check-out (top-bar clock, notification) lands here with ?fix=
  // YYYY-MM-DD: show that day in My History and open its correction form.
  const [searchParams, setSearchParams] = useSearchParams();
  const fixDay = searchParams.get("fix");
  useEffect(() => {
    if (!fixDay) return;
    setTab("my");
    setHistoryPeriod("custom");
    setCustomFrom(fixDay);
    setCustomTo(fixDay);
  }, [fixDay]);
  useEffect(() => {
    if (!fixDay) return;
    const record = history.find((r) => r.date.slice(0, 10) === fixDay && r.checkIn);
    if (!record) return;
    setCorrectionRequestFor(record.id);
    setCorrectionErr("");
    // Once open, the link has done its job; a refresh should not reopen it.
    setSearchParams((p) => { p.delete("fix"); return p; }, { replace: true });
  }, [fixDay, history, setSearchParams]);

  async function loadTodayRecord() {
    try {
      const { data } = await api.get<{ data: AttendanceRecord[] }>(`/attendance/me?limit=1`);
      setTodayRecord(data.data[0] ?? null);
    } catch (err) { logError("AttendancePage.loadTodayRecord", err); }
  }

  async function loadCurrentShift() {
    try {
      const { data } = await api.get<ShiftRecord | null>(`/employees/${employeeId}/shift/current`);
      setCurrentShift(data);
    } catch (err) { logError("AttendancePage.loadCurrentShift", err); }
  }

  async function loadHistory() {
    setLoading(true);
    try {
      const { from, to } = historyPeriod === "custom"
        ? { from: customFrom, to: customTo }
        : rangeFor(historyPeriod, historyAnchor);
      const params = new URLSearchParams({ limit: "100" });
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const { data } = await api.get<{ data: AttendanceRecord[]; hasAnyRecords: boolean }>(`/attendance/me?${params}`);
      setHistory(data.data);
      setHasAnyRecords(data.hasAnyRecords);
    } catch (err) { logError("AttendancePage.loadHistory", err); } finally { setLoading(false); }
  }

  async function loadGate() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (gateSearch.trim()) params.set("search", gateSearch.trim());
      if (gateOfficeId) params.set("officeId", gateOfficeId);
      const { data } = await api.get<GateEntry[]>(`/attendance/gate-register?${params}`);
      setGate(data);
    } catch (err) { logError("AttendancePage.loadGate", err); } finally { setLoading(false); }
  }

  async function loadMyCorrections() {
    try {
      const { data } = await api.get<{ data: MyCorrection[] }>("/attendance/corrections?mine=true");
      setMyCorrections(data.data);
    } catch (err) { logError("AttendancePage.loadMyCorrections", err); }
  }

  async function loadMyWeekly() {
    try {
      const { data } = await api.get<WeeklyHoursSelf>(`/attendance/weekly-hours?weekStart=${weekStart}`);
      setMyWeekly(data);
    } catch (err) { logError("AttendancePage.loadMyWeekly", err); }
  }

  /** Prev/next week. The list follows the week into another month, so the two controls agree. */
  function moveWeek(delta: number) {
    const next = shiftWeek(weekStart, delta);
    setWeekStart(next);
    setWeeklyHistoryAnchor(monthOfWeek(next));
  }

  /** Prev/next month. The selected week moves into that month, so the week nav shows where the list is. */
  function moveMonth(delta: number) {
    const next = shiftAnchor("month", weeklyHistoryAnchor, delta);
    setWeeklyHistoryAnchor(next);
    setWeekStart(firstWeekOfMonth(next));
  }

  async function loadWeeklyHistory() {
    setWeeklyHistoryLoading(true);
    try {
      const { from, to } = rangeFor("month", weeklyHistoryAnchor);
      if (!from || !to) { setWeeklyHistory([]); setWeeklyHasAnyRecords(true); return; }
      const params = new URLSearchParams({ from, to });
      const { data } = await api.get<{ data: WeeklyHoursHistoryRow[]; hasAnyRecords: boolean }>(`/attendance/weekly-hours/history?${params}`);
      setWeeklyHistory(data.data);
      setWeeklyHasAnyRecords(data.hasAnyRecords);
    } catch (err) { logError("AttendancePage.loadWeeklyHistory", err); } finally { setWeeklyHistoryLoading(false); }
  }

  async function submitWeeklyCorrection(row: WeeklyHoursHistoryRow) {
    if (!weeklyCorrectionReason.trim()) { setWeeklyCorrectionErr("Enter a reason"); throw new Error("validation"); }
    if (!weeklyCorrectionHours.trim()) { setWeeklyCorrectionErr("Enter corrected total hours"); throw new Error("validation"); }
    setWeeklyCorrectionBusy(true); setWeeklyCorrectionErr("");
    try {
      if (row.correctionStatus === "PENDING" && row.correctionId) {
        await api.patch(`/attendance/weekly-hours-corrections/${row.correctionId}`, {
          requestedTotalHours: Number(weeklyCorrectionHours),
          reason: weeklyCorrectionReason.trim(),
        });
      } else {
        await api.post("/attendance/weekly-hours-corrections", {
          weekStart: row.weekStart,
          requestedTotalHours: Number(weeklyCorrectionHours),
          reason: weeklyCorrectionReason.trim(),
        });
      }
      setWeeklyCorrectionRequestFor(null); setWeeklyCorrectionHours(""); setWeeklyCorrectionReason("");
      loadWeeklyHistory();
    } catch (e: unknown) {
      setWeeklyCorrectionErr(errMsg(e) ?? "Failed to submit — try again");
      throw e;
    } finally { setWeeklyCorrectionBusy(false); }
  }

  function editWeeklyCorrection(row: WeeklyHoursHistoryRow) {
    setWeeklyCorrectionRequestFor(row.weekStart);
    setWeeklyCorrectionHours(row.requestedTotalHours != null ? String(row.requestedTotalHours) : "");
    setWeeklyCorrectionReason(row.correctionReason ?? "");
    setWeeklyCorrectionErr("");
  }

  // Cached per week, so leaving the Team tab and coming back shows the list at once.
  // `force` drops the cached list first, for when an approval has changed its counts.
  async function loadTeamOverview(force = false) {
    const key = `team-overview|${weekStart}`;
    if (force) invalidateCached(key);
    const hit = getCached<TeamOverviewRow[]>(key);
    if (hit !== undefined) { setTeamOverview(hit); return; }
    setTeamOverviewLoading(true);
    try {
      setTeamOverview(await fetchCached(key, async () => (await api.get<TeamOverviewRow[]>(`/attendance/team-overview?weekStart=${weekStart}`)).data));
    } catch (err) { logError("AttendancePage.loadTeamOverview", err); } finally { setTeamOverviewLoading(false); }
  }

  function refreshSelectedMember() {
    reloadMember();
    loadTeamOverview(true);
  }

  async function submitCorrection(r: AttendanceRecord) {
    if (!correctionReason.trim()) { setCorrectionErr("Enter a reason"); throw new Error("validation"); }
    if (!correctionCheckIn && !correctionCheckOut) { setCorrectionErr("Enter a corrected check-in or check-out time"); throw new Error("validation"); }
    setCorrectionBusy(true); setCorrectionErr("");
    try {
      const dateOnly = r.date.slice(0, 10);
      const payload: Record<string, unknown> = { reason: correctionReason.trim() };
      if (correctionCheckIn)  payload.requestedCheckIn  = new Date(`${dateOnly}T${correctionCheckIn}:00+05:30`).toISOString();
      if (correctionCheckOut) payload.requestedCheckOut = new Date(`${dateOnly}T${correctionCheckOut}:00+05:30`).toISOString();
      const pending = myCorrections.find((c) => c.date.slice(0, 10) === dateOnly && c.status === "PENDING");
      if (pending) {
        await api.patch(`/attendance/corrections/${pending.id}`, payload);
      } else {
        await api.post("/attendance/corrections", { date: dateOnly, ...payload });
      }
      setCorrectionRequestFor(null); setCorrectionCheckIn(""); setCorrectionCheckOut(""); setCorrectionReason("");
      loadMyCorrections();
    } catch (e: unknown) {
      setCorrectionErr(errMsg(e) ?? "Failed to submit — try again");
      throw e;
    } finally { setCorrectionBusy(false); }
  }

  function editCorrection(record: AttendanceRecord, correction: MyCorrection) {
    setCorrectionRequestFor(record.id);
    setCorrectionCheckIn(correction.requestedCheckIn ? new Date(correction.requestedCheckIn).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: IST, hour12: false }) : "");
    setCorrectionCheckOut(correction.requestedCheckOut ? new Date(correction.requestedCheckOut).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: IST, hour12: false }) : "");
    setCorrectionReason(correction.reason);
    setCorrectionErr("");
  }

  useEffect(() => {
    loadTodayRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!employeeId || isUS) return;
    loadCurrentShift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, isUS]);

  useEffect(() => {
    if (tab === "my") loadMyCorrections();
    else if (tab === "gate") loadGate();
    else if (tab === "team") { if (canApprove) loadTeamOverview(); }
    else loadMyWeekly();
    // canApprove derives from the role in MeContext, which is empty until /me/profile
    // resolves; without it here a refresh on the Team tab would never load the overview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, weekStart, canApprove]);

  useEffect(() => {
    if (tab === "my") loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, historyPeriod, historyAnchor, customFrom, customTo]);

  useEffect(() => {
    if (tab === "weekly") loadWeeklyHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, weeklyHistoryAnchor]);

  useEffect(() => {
    if (tab !== "gate") return;
    const t = setTimeout(loadGate, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateSearch, gateOfficeId]);

  useEffect(() => {
    if (!canViewGate) return;
    api.get<OfficeOption[]>("/admin/offices").then(r => setOffices(r.data)).catch(() => { /* the gate office filter just won't populate */ });
  }, [canViewGate]);

  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ tab })); } catch { /* private browsing */ }
  }, [PREFS_KEY, tab]);

  async function doAction(type: "checkin" | "checkout") {
    try {
      let payload: Record<string, unknown> = type === "checkin" ? { source: "WEB" } : {};
      if (type === "checkin" && navigator.geolocation) {
        const pos = await new Promise<GeolocationPosition | null>(resolve => {
          navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), { timeout: 5000 });
        });
        if (pos) payload = { ...payload, geoLat: pos.coords.latitude, geoLong: pos.coords.longitude };
      }
      const { data: record } = await api.post<AttendanceRecord>(`/attendance/${type}`, payload);
      setTodayRecord(record);
      if (type === "checkout") {
        toast.show(`Checked out successfully — worked ${duration(record.checkIn, record.checkOut)}`, "success");
      } else {
        toast.show("Checked in successfully", "success");
      }
      loadTodayRecord();
      // The top-bar clock holds its own copy of today's record; without this it
      // would still show "not checked in" and keep Check Out disabled.
      notifyAttendanceChanged();
      if (tab === "my") loadHistory();
    } catch (e: unknown) {
      toast.show(errMsg(e) ?? "Error", "error");
      throw e;
    }
  }

  // The same rules as the top-bar clock (attendanceActions).
  const { canCheckIn, canCheckOut } = attendanceActions(todayRecord, hasShift);

  if (isUS) return <ManagedInADP module="Attendance" />;

  const durationSubText = shiftDelta(todayRecord?.checkIn ?? null, todayRecord?.checkOut ?? null, todayRecord?.shiftHours ?? null);

  // The status strip stays on a single horizontal line at every width: instead of
  // wrapping onto a second row, each segment scales down (clamp() on the chip,
  // gaps, padding and type) and ellipsizes what still does not fit. The buttons
  // keep a usable size -- only the readouts give up space.
  // These readouts are page chrome, not a status, so they carry the app's own
  // brand blue rather than the purple --color-accent-* family, which is reserved
  // for the "leave"/"personal" category elsewhere. The tint is mixed from the
  // brand colour instead of a fixed step so it lifts off the card by the same
  // amount in light and dark mode (see the .shift-window precedent in
  // tokens.css); --color-brand-50 is almost exactly --color-surface-0 in dark.
  const chipStyle: React.CSSProperties = { width: "clamp(26px, 2.4vw, 36px)", height: "clamp(26px, 2.4vw, 36px)", borderRadius: "var(--radius-md)", background: "color-mix(in srgb, var(--color-brand-500) 14%, transparent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
  const chipIconColor = "var(--color-brand-500)";
  const statLabelStyle: React.CSSProperties = { fontSize: "clamp(9px, 0.72vw, 11px)", fontWeight: 600, color: "var(--color-surface-700)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
  const dividerStyle: React.CSSProperties = { width: 1, alignSelf: "stretch", margin: "var(--space-3) 0", background: "var(--color-surface-200)", flexShrink: 0 };
  const statSegmentStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: "clamp(var(--space-1), 0.8vw, var(--space-3))", padding: "clamp(var(--space-2), 1vw, var(--space-4))", minWidth: 0 };
  const statTextStyle: React.CSSProperties = { minWidth: 0 };
  const statValueStyle: React.CSSProperties = { fontSize: "clamp(13px, 1.15vw, 18px)", fontWeight: 700, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
  const statCaptionStyle: React.CSSProperties = { fontSize: "clamp(9px, 0.7vw, 11px)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

  const workingDays = myWeekly?.days.filter((d) => d.expectedHours > 0) ?? [];
  const daysWorked = workingDays.filter((d) => d.actualHours > 0).length;
  const weeklyStatus = weeklyStatusDisplay(myWeekly?.status ?? null, myWeekly?.totalHours ?? null, myWeekly?.expectedHours ?? null);

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-surface-50)", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "var(--space-2) clamp(var(--space-4), 4vw, var(--space-8)) var(--space-2)", display: "flex", alignItems: "baseline", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <h1 style={{ fontSize: "20px", fontWeight: 700 }}>Attendance</h1>
        <p style={{ fontSize: "13px", color: "var(--color-surface-700)", marginLeft: "auto" }}>
          {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: IST })}
        </p>
      </div>

      <div style={{ padding: "0 clamp(var(--space-4), 4vw, var(--space-8))" }}>
        {/* "both" gives one sweep around the border when the page loads and then
            follows the cursor; "ultra" keeps that to the faintest edge light, since
            this bar sits above everything else and should not compete with it. */}
        <GlowCard
          size="none"
          glow="ultra"
          spotlight="low"
          interaction="both"
          radius="lg"
          className="flex flex-nowrap items-center shadow-sm"
        >
          <div style={statSegmentStyle}>
            <div style={chipStyle}><LogIn size={16} color={chipIconColor} /></div>
            <div style={statTextStyle}>
              <div style={statLabelStyle}>Check In</div>
              <div style={statValueStyle}>{fmt(todayRecord?.checkIn ?? null)}</div>
            </div>
          </div>
          <div style={dividerStyle} />

          <div style={statSegmentStyle}>
            <div style={chipStyle}><LogOut size={16} color={chipIconColor} /></div>
            <div style={statTextStyle}>
              <div style={statLabelStyle}>Check Out</div>
              <div style={statValueStyle}>{fmt(todayRecord?.checkOut ?? null)}</div>
            </div>
          </div>
          <div style={dividerStyle} />

          <div style={statSegmentStyle}>
            <div style={chipStyle}><Timer size={16} color={chipIconColor} /></div>
            <div style={statTextStyle}>
              <div style={statLabelStyle}>Duration</div>
              <div style={statValueStyle}>{duration(todayRecord?.checkIn ?? null, todayRecord?.checkOut ?? null)}</div>
              {durationSubText && <div style={{ ...statCaptionStyle, color: "var(--color-warning)" }}>{durationSubText}</div>}
            </div>
          </div>
          <div style={dividerStyle} />

          {currentShift && (
            <>
              <div style={statSegmentStyle}>
                <div style={chipStyle}><CalendarClock size={16} color={chipIconColor} /></div>
                <div style={statTextStyle}>
                  <div style={statLabelStyle}>Current Shift</div>
                  <div style={{ ...statValueStyle, display: "flex", alignItems: "center", gap: 8 }}>
                    {currentShift.shiftStartHHMM} – {currentShift.shiftEndHHMM}
                    {/* The hours differ from the roster today, and nothing else on the
                        bar would say why. The caption below gives the end date. */}
                    {currentShift.temporary && <Badge tone="info">Approved</Badge>}
                  </div>
                  {currentShift.temporary ? (
                    <div
                      style={{ ...statCaptionStyle, color: "var(--color-info-fg)" }}
                      title={
                        currentShift.setByName
                          ? `Approved by ${currentShift.setByName}: ${currentShift.temporary.remark}`
                          : currentShift.temporary.remark
                      }
                    >
                      Until {shortDay(currentShift.temporary.endDate)} &middot; then back to your usual hours
                    </div>
                  ) : (
                    /* Names the role that actually assigned it -- a senior manager or HR
                       reading "Set by manager" would be wrong. Falls back to the generic
                       wording only when the setter can no longer be resolved. */
                    <div
                      style={{ ...statCaptionStyle, color: "var(--color-surface-500)" }}
                      title={currentShift.setByName ? `Set by ${currentShift.setByName}` : undefined}
                    >
                      Set by {exactRoleLabel(currentShift.setByRole ?? null) ?? "manager"}
                    </div>
                  )}
                </div>
              </div>
              <div style={dividerStyle} />
            </>
          )}

          <div style={{ ...statSegmentStyle, marginLeft: "auto", flexShrink: 0 }}>
            <AsyncButton
              variant="success"
              disabled={!canCheckIn}
              onClick={() => doAction("checkin")}
              doneLabel="Checked In"
              size="default"
              radius="md"
              border="none"
              hoverBorder="accent"
              background="variant"
              completionIntensity="subtle"
              transitionDuration={150}
              progressPace={2000}
              className="h-9 shrink-0 whitespace-nowrap px-3 text-xs sm:h-10 sm:px-4 sm:text-sm"
            >
              Check In
            </AsyncButton>
            <AsyncButton
              variant="warning"
              disabled={!canCheckOut}
              onClick={() => doAction("checkout")}
              doneLabel="Checked Out"
              size="default"
              radius="md"
              border="none"
              hoverBorder="accent"
              background="variant"
              completionIntensity="subtle"
              transitionDuration={150}
              progressPace={2000}
              className="h-9 shrink-0 whitespace-nowrap px-3 text-xs sm:h-10 sm:px-4 sm:text-sm"
            >
              Check Out
            </AsyncButton>
          </div>
        </GlowCard>
      </div>

      {/* At least the viewport below the top header, so the page can always be
          scrolled far enough for the sticky tab bar to reach the top -- without
          it a short tab (or a collapsed Summary) leaves too little content to
          scroll past the title and check-in card. */}
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: "calc(100vh - var(--app-header-h))" }}>
        {/* Sticky so the title and today's check-in card scroll away while the tabs
            stay reachable. Offset by the TopHeaderBar's height (it is sticky at
            top: 0 in AppShell's <main>, the scroll container) and kept below its
            z-40 so it tucks under the header rather than over it. */}
        <div style={{ position: "sticky", top: "var(--app-header-h)", zIndex: 30, background: "var(--color-surface-50)", padding: "var(--space-5) var(--space-8) var(--space-2)", display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
          <SegmentedTabs
            value={tab}
            onChange={(v) => setTab(v as "my" | "biometric" | "gate" | "weekly" | "shift" | "team" | "monthly")}
            items={[
              { value: "my", label: "My History" },
              { value: "biometric", label: "Weekly Biometric" },
              ...(canViewGate ? [{ value: "gate", label: "Gate Register (Today)" }] : []),
              ...(attendanceApprovalsEnabled ? [{ value: "weekly", label: "Weekly Hours" }] : []),
              { value: "shift", label: "My Shift" },
              ...(canApprove ? [{ value: "team", label: "Team", badge: pendingTeamMembers.length || undefined }] : []),
              ...(canViewMonthly ? [{ value: "monthly", label: "All Team" }] : []),
            ]}
          />
          {tab === "gate" && (
            <>
              <Input
                value={gateSearch}
                onChange={e => setGateSearch(e.target.value)}
                placeholder="Search by name or employee code…"
                className="w-[220px]"
              />
              {offices.length > 0 && (
                <DropdownSelect
                  value={gateOfficeId}
                  onChange={v => setGateOfficeId(v)}
                  placeholder="All Offices"
                  options={offices.map(o => ({ value: o.id, label: o.name }))}
                  open={openFilter === "gateOffice"}
                  onToggle={() => setOpenFilter(f => (f === "gateOffice" ? null : "gateOffice"))}
                  onClose={() => setOpenFilter(null)}
                />
              )}
            </>
          )}
        </div>

        {tab === "weekly" && (
          <div style={{ padding: "var(--space-1) var(--space-8) var(--space-8)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <button
              onClick={() => setStatsCollapsed(c => !c)}
              style={{ display: "flex", alignItems: "center", alignSelf: "flex-end", gap: 6, background: "none", border: "none", padding: "4px 0", marginBottom: statsCollapsed ? "var(--space-2)" : "var(--space-3)", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--color-surface-700)", flexShrink: 0 }}
            >
              {statsCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />} Summary
            </button>
            {!statsCollapsed && (
            <div className="mb-3 flex shrink-0 flex-wrap gap-3">
              <SummaryStatCard
                layout="row"
                icon={CalendarCheck}
                tone="brand"
                label="Days Worked"
                value={<>{daysWorked}<SummaryStatSuffix> / {workingDays.length}</SummaryStatSuffix></>}
                caption={myWeekly && `${myWeekly.totalHours.toFixed(1)} hrs worked`}
                className="flex-1"
              />

              <SummaryStatCard
                layout="row"
                icon={Timer}
                tone="success"
                label="Week Hours"
                value={<>{myWeekly ? myWeekly.totalHours.toFixed(1) : "—"}<SummaryStatSuffix> / {myWeekly ? myWeekly.expectedHours.toFixed(1) : "—"} hrs</SummaryStatSuffix></>}
                className="flex-1"
              />

              <SummaryStatCard
                layout="row"
                icon={FileClock}
                tone={weeklyStatus.tone}
                iconTone="warning"
                label="Current Week Status"
                value={weeklyStatus.label}
                valueClassName={cn("text-[12px] whitespace-nowrap", weeklyStatus.textClass)}
                caption={myWeekly && `${isoWeekLabel(myWeekly.weekStart)} · ${myWeekly.totalHours.toFixed(1)}/${myWeekly.expectedHours.toFixed(1)} hrs`}
                className="flex-1"
              />
            </div>
            )}

            <WeeklyHoursTimeline
              loading={weeklyHistoryLoading}
              history={weeklyHistory}
              hasAnyRecords={weeklyHasAnyRecords}
              selectedWeekStart={weekStart}
              onSelectWeek={setWeekStart}
              weekNav={(
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                  <SpecularButton onClick={() => moveWeek(-1)}>&larr; Prev Week</SpecularButton>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{isoWeekLabel(weekStart)}</div>
                  <SpecularButton onClick={() => moveWeek(1)}>Next Week &rarr;</SpecularButton>
                </div>
              )}
              rangeLabel={rangeFor("month", weeklyHistoryAnchor).label}
              rangeFrom={rangeFor("month", weeklyHistoryAnchor).from}
              rangeTo={rangeFor("month", weeklyHistoryAnchor).to}
              onPrev={() => moveMonth(-1)}
              onNext={() => moveMonth(1)}
              correctionEditor={attendanceApprovalsEnabled ? {
                requestFor: weeklyCorrectionRequestFor,
                onRequest: (ws) => { setWeeklyCorrectionRequestFor(ws); setWeeklyCorrectionErr(""); },
                onEdit: editWeeklyCorrection,
                hours: weeklyCorrectionHours,
                onHoursChange: setWeeklyCorrectionHours,
                reason: weeklyCorrectionReason,
                onReasonChange: setWeeklyCorrectionReason,
                busy: weeklyCorrectionBusy,
                err: weeklyCorrectionErr,
                onSubmit: submitWeeklyCorrection,
                onCancel: () => { setWeeklyCorrectionRequestFor(null); setWeeklyCorrectionHours(""); setWeeklyCorrectionReason(""); setWeeklyCorrectionErr(""); },
              } : undefined}
            />
          </div>
        )}

        {/* Its own tab: asking about your shift is a separate errand from reading
            your attendance history, and the form crowded that list. */}
        {tab === "shift" && (
          <div style={{ padding: "var(--space-4) clamp(var(--space-4), 4vw, var(--space-8)) var(--space-8)" }}>
            {employeeId && <MyShiftRequestsCard employeeId={employeeId} />}
          </div>
        )}

        {tab === "team" && (
          <div style={{ padding: "var(--space-4) var(--space-8) var(--space-8)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            {selectedMemberId ? memberDetail ? (
            <TeamMemberDetailPanel
              memberDetail={memberDetail}
              memberWeeklyCorrections={memberWeeklyCorrections}
              memberCorrections={memberCorrections}
              memberOvertime={memberOvertime}
              memberShiftDayRequests={memberShiftDayRequests}
              memberHistory={memberHistory}
              onRefresh={refreshSelectedMember}
              onBack={() => setSelectedMemberId(null)}
              backLabel="Back to Team"
            />
            ) : (
            <div style={{ textAlign: "center", padding: "var(--space-12)", color: "var(--color-surface-700)" }}>Loading…</div>
            ) : (
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "var(--space-4)", flex: 1, minHeight: 0, overflowY: "auto", alignContent: "start", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "var(--space-3)" }}>
              {teamBusy ? (
                Array.from({ length: 6 }).map((_, i) => <TeamMemberCardSkeleton key={i} />)
              ) : pendingTeamMembers.map((m) => (
                <TeamMemberCard
                  key={m.id}
                  person={m}
                  context="review"
                  selected={selectedMemberId === m.id}
                  onSelect={() => setSelectedMemberId(m.id)}
                  card={{ size: "sm", glow: "ultra", spotlight: "medium", spotlightSize: "md", tone: "aurora" }}
                />
              ))}
              {!teamBusy && pendingTeamMembers.length === 0 && <div style={{ color: "var(--color-surface-700)" }}>No pending requests from your team.</div>}
            </div>
            )}
          </div>
        )}

        {tab === "monthly" && canViewMonthly && <MonthlyAttendanceGrid weekStart={weekStart} />}

        {tab === "my" && (
          <div style={{ padding: "var(--space-4) var(--space-8) var(--space-8)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <AttendanceHistoryTimeline
              view={historyView}
              onViewChange={setHistoryView}
              loading={loading}
              history={history}
              hasAnyRecords={hasAnyRecords}
              myCorrections={myCorrections}
              period={historyPeriod}
              onPeriodChange={(p) => { setHistoryPeriod(p); setHistoryAnchor(currentAnchor(p === "custom" ? "week" : p)); }}
              rangeLabel={historyPeriod === "custom" ? "Custom range" : rangeFor(historyPeriod, historyAnchor).label}
              onPrev={() => setHistoryAnchor((a) => shiftAnchor(historyPeriod === "custom" ? "week" : historyPeriod, a, -1))}
              onNext={() => setHistoryAnchor((a) => shiftAnchor(historyPeriod === "custom" ? "week" : historyPeriod, a, 1))}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
              rangeFrom={historyPeriod === "custom" ? customFrom : rangeFor(historyPeriod, historyAnchor).from}
              rangeTo={historyPeriod === "custom" ? customTo : rangeFor(historyPeriod, historyAnchor).to}
              correctionEditor={attendanceApprovalsEnabled ? {
                requestFor: correctionRequestFor,
                onRequest: (id) => { setCorrectionRequestFor(id); setCorrectionErr(""); },
                onEdit: editCorrection,
                checkIn: correctionCheckIn,
                onCheckInChange: setCorrectionCheckIn,
                checkOut: correctionCheckOut,
                onCheckOutChange: setCorrectionCheckOut,
                reason: correctionReason,
                onReasonChange: setCorrectionReason,
                busy: correctionBusy,
                err: correctionErr,
                onSubmit: submitCorrection,
                onCancel: () => { setCorrectionRequestFor(null); setCorrectionCheckIn(""); setCorrectionCheckOut(""); setCorrectionReason(""); setCorrectionErr(""); },
              } : undefined}
            />
          </div>
        )}

        {tab === "biometric" && (
          <div style={{ padding: "var(--space-4) var(--space-8) var(--space-8)" }}>
            <WeeklyBiometricAttendance />
          </div>
        )}

        {tab === "gate" && (
        <div style={{ padding: "var(--space-4) var(--space-8) var(--space-8)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          {loading ? (
            // The finished card with its real header and pipeline rows, blocks in place
            // of the values: same column widths and row height, so nothing shifts when
            // the gate register lands.
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "var(--space-4)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <div style={{ display: "flex", flexWrap: "nowrap", marginBottom: "var(--space-2)", flexShrink: 0 }}>
                <div style={{ width: 4, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, padding: "var(--space-1) var(--space-2)" }}>
                  <div style={{ display: "flex", flexWrap: "nowrap" }}>
                    <PipelineCell width={GATE_COLS.employee} divider={false}><ColHeader>Employee</ColHeader></PipelineCell>
                    <PipelineCell width={GATE_COLS.department}><ColHeader>Department</ColHeader></PipelineCell>
                    <PipelineCell width={GATE_COLS.office}><ColHeader>Office</ColHeader></PipelineCell>
                    <PipelineCell width={GATE_COLS.checkIn}><ColHeader>Check In</ColHeader></PipelineCell>
                    <PipelineCell width={GATE_COLS.checkOut}><ColHeader>Check Out</ColHeader></PipelineCell>
                    <PipelineCell width={GATE_COLS.duration} grow><ColHeader>Duration</ColHeader></PipelineCell>
                    <div style={{ width: 120, flexShrink: 0 }} />
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", overflow: "hidden", display: "flex" }}>
                    <div style={{ width: 4, background: "var(--color-surface-200)", flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0, padding: "var(--space-2)" }}>
                      <div style={{ display: "flex", alignItems: "stretch", flexWrap: "nowrap" }}>
                        <PipelineCell width={GATE_COLS.employee} divider={false}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Skeleton width={120} height={13} />
                            <Skeleton width={80} height={11} />
                          </div>
                        </PipelineCell>
                        <PipelineCell width={GATE_COLS.department}><Skeleton width={90} height={13} /></PipelineCell>
                        <PipelineCell width={GATE_COLS.office}><Skeleton width={70} height={13} /></PipelineCell>
                        <PipelineCell width={GATE_COLS.checkIn}><Skeleton width={64} height={14} /></PipelineCell>
                        <PipelineCell width={GATE_COLS.checkOut}><Skeleton width={64} height={14} /></PipelineCell>
                        <PipelineCell width={GATE_COLS.duration} grow><Skeleton width={70} height={14} /></PipelineCell>
                        <div style={{ width: 120, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "0 0 0 var(--space-3)", borderLeft: "1px solid var(--color-surface-100)" }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "var(--space-4)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              {/* The header sits outside the scroller on the same 4px + space-2 offset
                  as the rows below it, so the columns line up. */}
              <div style={{ display: "flex", flexWrap: "nowrap", marginBottom: "var(--space-2)", flexShrink: 0 }}>
                <div style={{ width: 4, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, padding: "var(--space-1) var(--space-2)" }}>
                  <div style={{ display: "flex", flexWrap: "nowrap" }}>
                    <PipelineCell width={GATE_COLS.employee} divider={false}><ColHeader>Employee</ColHeader></PipelineCell>
                    <PipelineCell width={GATE_COLS.department}><ColHeader>Department</ColHeader></PipelineCell>
                    <PipelineCell width={GATE_COLS.office}><ColHeader>Office</ColHeader></PipelineCell>
                    <PipelineCell width={GATE_COLS.checkIn}><ColHeader>Check In</ColHeader></PipelineCell>
                    <PipelineCell width={GATE_COLS.checkOut}><ColHeader>Check Out</ColHeader></PipelineCell>
                    <PipelineCell width={GATE_COLS.duration} grow><ColHeader>Duration</ColHeader></PipelineCell>
                    <div style={{ width: 120, flexShrink: 0 }} />
                  </div>
                </div>
              </div>

              <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
                {gate.length === 0 ? (
                  <div style={{ padding: "var(--space-8)", textAlign: "center", color: "var(--color-surface-700)" }}>No one checked in today.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                    {gate.map((r) => (
                      <div key={r.id} style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", overflow: "hidden", display: "flex" }}>
                        {/* The leading bar carries the row's state, as it does in the other two lists. */}
                        <div style={{ width: 4, background: r.isLate ? "var(--color-error)" : "var(--color-success)", flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0, padding: "var(--space-2)" }}>
                          <div style={{ display: "flex", alignItems: "stretch", flexWrap: "nowrap" }}>
                            <PipelineCell width={GATE_COLS.employee} divider={false}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, width: "100%" }}>
                                <div
                                  style={{ fontSize: 13, lineHeight: 1.15, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                  onClick={() => navigate(`/employees/${r.employee.id}/edit`)}
                                  onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")}
                                  onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}
                                >
                                  {r.employee.firstName} {r.employee.lastName}
                                </div>
                                <div style={{ fontSize: 11, lineHeight: 1, color: "var(--color-surface-700)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.employee.employeeCode}</div>
                              </div>
                            </PipelineCell>
                            <PipelineCell width={GATE_COLS.department}>
                              <span style={{ fontSize: 13, color: "var(--color-surface-700)" }}>{r.employee.department?.name ?? "—"}</span>
                            </PipelineCell>
                            <PipelineCell width={GATE_COLS.office}>
                              <span style={{ fontSize: 13, color: "var(--color-surface-700)" }}>{r.employee.office?.name ?? "—"}</span>
                            </PipelineCell>
                            <PipelineCell width={GATE_COLS.checkIn}>{fmt(r.checkIn)}</PipelineCell>
                            <PipelineCell width={GATE_COLS.checkOut}>{fmt(r.checkOut)}</PipelineCell>
                            <PipelineCell width={GATE_COLS.duration} grow>
                              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                                <span style={{ fontSize: 13, lineHeight: 1.15 }}>{duration(r.checkIn, r.checkOut)}</span>
                                {shiftDelta(r.checkIn, r.checkOut, r.shiftHours) && (
                                  <span style={{ fontSize: 11, lineHeight: 1, color: "var(--color-warning)" }}>{shiftDelta(r.checkIn, r.checkOut, r.shiftHours)}</span>
                                )}
                              </div>
                            </PipelineCell>
                            <div style={{ width: 120, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, padding: "0 0 0 var(--space-3)", borderLeft: "1px solid var(--color-surface-100)" }}>
                              {r.isLate && <span style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "3px 10px", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600 }}>Late</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
